import { useQuery } from '@tanstack/react-query';

import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { ConsumerRedeemableTicket } from './types';

const useListRedeemableTicketsFromPurchasedCart = ({
  cartId,
  email,
}: {
  cartId?: string;
  email?: string;
}) => {
  const StoreApi = useStoreApi();

  const encodedEmail = encodeURIComponent(email ?? '');

  const getRedeemableTickets = async () => {
    const { data } = await StoreApi.get<{ tickets: ConsumerRedeemableTicket[] }>(
      `/consumer/${cartId}/tickets?email=${encodedEmail}`
    );

    return data.tickets;
  };

  const key = cartId
    ? storeKeys.id('user').consumer.postCheckoutCarts.id(cartId).redeemableTickets.all
    : [];

  return useQuery({
    queryKey: key,
    queryFn: getRedeemableTickets,
    enabled: !!cartId,
  });
};

const useShowRedeemableTicketFromPurchasedCart = ({
  cartId,
  ticketId,
}: {
  cartId?: string;
  ticketId?: string;
}) => {
  const StoreApi = useStoreApi();

  const getRedeemableTicket = async () => {
    const { data } = await StoreApi.get<{ ticket: ConsumerRedeemableTicket }>(
      `/consumer/${cartId}/tickets/${ticketId}`
    );

    return data.ticket;
  };

  const key =
    !!cartId && !!ticketId
      ? storeKeys.id('user').consumer.postCheckoutCarts.id(cartId).redeemableTickets.id(ticketId)
          .detail
      : [];

  return useQuery({
    queryKey: key,
    queryFn: getRedeemableTicket,
  });
};

const useListRedeemableTicketsByEvent = ({ eventId }: { eventId?: string }) => {
  const StoreApi = useStoreApi();

  const getRedeemableTickets = async () => {
    const { data } = await StoreApi.get<{ tickets: ConsumerRedeemableTicket[] }>(
      `/consumer/events/${eventId}/tickets`
    );

    return data.tickets;
  };

  const key = eventId ? storeKeys.id('user').consumer.events.id(eventId).redeemableTickets : [];

  return useQuery({
    queryKey: key,
    queryFn: getRedeemableTickets,
    enabled: !!eventId,
  });
};

export {
  useListRedeemableTicketsByEvent,
  useListRedeemableTicketsFromPurchasedCart,
  useShowRedeemableTicketFromPurchasedCart,
};
